import React, { useCallback } from "react";

import { useRouter } from "next/router";
import CamIcon from "public/icons/cam.svg";
import GiftIcon from "public/icons/gift.svg";
import GradeActiveIcon from "public/icons/grade-active.svg";
import GradeIcon from "public/icons/grade.svg";
import MailIcon from "public/icons/mail.svg";

import { Theme, useMediaQuery } from "@mui/material";

import { ButtonWithIcon } from "@/components/form/Button/ButtonWithIcon";
import { useOpenModal } from "@/components/modal/utils/openModal";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import { AuthRenderer } from "@/context/auth-renderer/AuthRenderer";
import { useFavorite } from "@/features/lady-profile/hooks";
import useChatWindow from "@/hooks/useChatOpen";
import { ConditionalRender } from "@/lib/conditional-render/ConditionalRender";
import { LadyMinV2DTO } from "@/models/lady-dtos";
import { clsxm } from "@/utils/tailwindMerge";

type PropTypes = {
  ladyDto: LadyMinV2DTO;
  skipFavoriteButton?: boolean;
  onClickHandler: (
    header: string,
  ) => (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  toggleFavoriteLady?: (id: number) => void;
};

export const SideButtonsList: React.FC<PropTypes> = ({
  ladyDto,
  skipFavoriteButton,
  onClickHandler,
  toggleFavoriteLady,
}) => {
  const { isGiftsAvailable, isVideochatAvailable, id, isFavorite } = ladyDto;
  const { onFavouriteClick } = useFavorite({
    objectId: id,
    isFavorite,
    favoriteType: "LADY",
    ignoreLocalState: true,
  });

  const isInFavorites = ladyDto.isFavorite;

  const handleFavoriteClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    toggleFavoriteLady && toggleFavoriteLady(ladyDto.id);
    onFavouriteClick(e);
  };

  const openChatWindow = useChatWindow(id, undefined, isVideochatAvailable);
  const router = useRouter();
  const handleNavigation =
    (path: string) => (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      router.push(path);
      e.stopPropagation();
    };
  const { openModal } = useOpenModal({
    disableBackGroundClick: true,
  });

  const authContext = useAuthContext();

  const downXs = useMediaQuery((theme: Theme) => theme.breakpoints.down(410));

  const iconButtonClass = clsxm(
    downXs && "w-7 h-7",
    "2xl:w-9 2xl:h-9",
    "bg-grey1-900 bg-opacity-40 hover:bg-grey1-900 hover:bg-opacity-40",
  );

  const onMailMeClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.stopPropagation();
      if (!ladyDto) return;

      if (authContext.isAuthenticated) {
        return openModal("CREATE_LETTER_MODAL", {
          ladyId: ladyDto.id,
          ladyDto: ladyDto,
        });
      } else {
        return onClickHandler("Mailing Me")(e);
      }
    },
    [authContext.isAuthenticated, ladyDto, onClickHandler, openModal],
  );

  return (
    <ul
      data-testid="cardActionButtons"
      className="flex flex-col gap-1 absolute right-1 top-1"
    >
      <AuthRenderer>
        <AuthRenderer.IsAuth>
          {skipFavoriteButton ? null : (
            <ButtonWithIcon
              onClick={handleFavoriteClick}
              ariaLabel={
                (isInFavorites ? "Remove lady from" : "Add lady to") +
                " favorite"
              }
              iconHoverColor="pink"
              disabledHover={isInFavorites}
              isActive={isInFavorites}
              Icon={isInFavorites ? GradeActiveIcon : GradeIcon}
              className={clsxm(
                isInFavorites && "!bg-white !hover:bg-white",
                iconButtonClass,
              )}
            />
          )}
        </AuthRenderer.IsAuth>
      </AuthRenderer>
      <ButtonWithIcon
        onClick={onMailMeClick}
        ariaLabel="Mail Me"
        iconHoverColor="pink"
        className={iconButtonClass}
        Icon={MailIcon}
      />
      <ConditionalRender on={isGiftsAvailable}>
        <ButtonWithIcon
          onClick={
            authContext.isAuthenticated
              ? handleNavigation("/send-a-gift")
              : onClickHandler("Send a gift")
          }
          iconHoverColor="pink"
          ariaLabel="Send a gift"
          className={iconButtonClass}
          Icon={GiftIcon}
        />
      </ConditionalRender>
      <ConditionalRender on={isVideochatAvailable}>
        <ButtonWithIcon
          className={iconButtonClass}
          iconHoverColor="pink"
          onClick={
            authContext.isAuthenticated
              ? () => openChatWindow("liveChat")
              : onClickHandler("Chatting with Me")
          }
          ariaLabel="Video Chat"
          Icon={CamIcon}
        />
      </ConditionalRender>
    </ul>
  );
};
